import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useOpportunityWizard } from './OpportunityWizardContext';
import OpportunityWizardLayout from './OpportunityWizardLayout';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Loader2, 
  Plus, 
  Search, 
  Trash, 
  Info, 
  X,
  ArrowUpDown,
  Check,
  SlidersHorizontal,
  Edit,
  Trash2 
} from 'lucide-react';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter 
} from '@/components/ui/dialog';
import { 
  Popover, 
  PopoverContent, 
  PopoverTrigger 
} from '@/components/ui/popover';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { format, addMonths, addYears } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { 
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious 
} from "@/components/ui/pagination";
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Tooltip, 
  TooltipContent, 
  TooltipProvider, 
  TooltipTrigger 
} from '@/components/ui/tooltip';
import { Checkbox } from '@/components/ui/checkbox';

interface SubscriptionPlanPrice {
  id: string;
  subscription_plan_id: string;
  currency: string;
  price_amount: number;
  cost_amount?: number;
  margin?: number;
}

interface SubscriptionPlan {
  id: string;
  name: string;
  price: number;
  currency: string;
  is_active: boolean;
  billing_frequency?: string;
  family_id?: string;
  sku?: string;
  description?: string;
  billing_period_type?: string;
  billing_period_duration?: number;
  prices?: SubscriptionPlanPrice[];
  subscription_plan_families?: {
    id: string;
    name: string;
  };
}

interface PlanFamily {
  id: string;
  name: string;
}

interface AdvancedFilters {
  billingFrequencies: string[];
  familyIds: string[];
}

type SortField = 'name' | 'price';
type SortDirection = 'asc' | 'desc';

interface SelectedPlan {
  subscription_plan_id: string;
  subscription_plan: SubscriptionPlan;
  quantity: number;
  unit_price: number;
  billing_period_unit: string;
  billing_period: string;
  current_term_start: string;
  current_term_end: string;
  billing_start_terms?: 'immediately' | 'custom date';
  auto_renew?: boolean;
}

const BILLING_PERIOD_UNITS = [
  { value: 'day', label: 'Dia(s)' },
  { value: 'week', label: 'Semana(s)' },
  { value: 'month', label: 'Mês(es)' },
  { value: 'year', label: 'Ano(s)' }
];

const ITEMS_PER_PAGE = 20;

// Função auxiliar para calcular preço total
const calculateTotalPrice = (plans: SelectedPlan[]) => {
  return plans.reduce((total, plan) => {
    return total + (plan.unit_price * plan.quantity);
  }, 0);
};

const translateBillingFrequency = (frequency: string): string => {
  const translations: Record<string, string> = {
    'monthly': 'Mensal',
    'yearly': 'Anual',
    'quarterly': 'Trimestral',
    'semiannually': 'Semestral'
  };
  
  return translations[frequency] || frequency;
};

const Step5Plans = () => {
  const navigate = useNavigate();
  const { state, addSelectedPlan, updateSelectedPlan, removeSelectedPlan } = useOpportunityWizard();
  
  const [subscriptionPlans, setSubscriptionPlans] = useState<SubscriptionPlan[]>([]);
  const [filteredPlans, setFilteredPlans] = useState<SubscriptionPlan[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  
  // New advanced modal related state
  const [isAddPlanDialogOpen, setIsAddPlanDialogOpen] = useState(false);
  const [planFamilies, setPlanFamilies] = useState<PlanFamily[]>([]);  
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState<AdvancedFilters>({
    billingFrequencies: [],
    familyIds: []
  });
  const [planSearchTerm, setPlanSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [totalPlans, setTotalPlans] = useState(0);
  
  // Original modal state
  const [isAddingPlan, setIsAddingPlan] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [unitPrice, setUnitPrice] = useState(0);
  const [billingPeriodUnit, setBillingPeriodUnit] = useState('month');
  const [billingPeriod, setBillingPeriod] = useState('12');
  const [currentTermStart, setCurrentTermStart] = useState(state.startDate);
  const [billingStartTerms, setBillingStartTerms] = useState<'immediately' | 'custom date'>('immediately');
  const [autoRenew, setAutoRenew] = useState(true);
  const [showCustomDateField, setShowCustomDateField] = useState(false);
  
  // Redirecionar se necessário
  useEffect(() => {
    if (!state.selectedCustomer) {
      navigate('../step1', { relative: 'path' });
    }
  }, [state.selectedCustomer, navigate]);

  useEffect(() => {
    fetchSubscriptionPlans();
  }, [planSearchTerm, advancedFilters, sortField, sortDirection, currentPage]);

  useEffect(() => {
    // Calculo de data final com base na data inicial e no período
    if (currentTermStart) {
      try {
        const startDate = new Date(currentTermStart);
        let endDate = startDate;
        
        const periodValue = parseInt(billingPeriod, 10);
        if (!isNaN(periodValue)) {
          if (billingPeriodUnit === 'day') {
            endDate = new Date(startDate.setDate(startDate.getDate() + periodValue));
          } else if (billingPeriodUnit === 'week') {
            endDate = new Date(startDate.setDate(startDate.getDate() + (periodValue * 7)));
          } else if (billingPeriodUnit === 'month') {
            endDate = addMonths(startDate, periodValue);
          } else if (billingPeriodUnit === 'year') {
            endDate = addYears(startDate, periodValue);
          }
        }
      } catch (error) {
        console.error('Error calculating term end date:', error);
      }
    }
  }, [currentTermStart, billingPeriod, billingPeriodUnit]);

  useEffect(() => {
    fetchPlanFamilies();
  }, []);

  const fetchSubscriptionPlans = async () => {
    try {
      setLoading(true);
      
      let query = supabase
        .from('subscription_plans')
        .select('*, prices:subscription_plan_prices(*), subscription_plan_families(*)')
        .eq('is_active', true);
      
      // Apply search filter if present
      if (planSearchTerm) {
        query = query.or(`name.ilike.%${planSearchTerm}%,sku.ilike.%${planSearchTerm}%`);
      }
      
      // Apply advanced filters if present
      if (advancedFilters.billingFrequencies.length > 0) {
        const billingFrequencyFilter = advancedFilters.billingFrequencies.map(
          freq => `billing_frequency.ilike.${freq}`
        ).join(',');
        query = query.or(billingFrequencyFilter);
      }
      
      if (advancedFilters.familyIds.length > 0) {
        query = query.in('family_id', advancedFilters.familyIds);
      }
      
      // Apply sorting
      const sortFieldMapping = {
        name: 'name',
        price: 'price'
      };
      
      query = query.order(sortFieldMapping[sortField], { ascending: sortDirection === 'asc' });
      
      // Calculate pagination range
      const from = (currentPage - 1) * ITEMS_PER_PAGE;
      const to = from + ITEMS_PER_PAGE - 1;
      
      // Apply pagination
      query = query.range(from, to);

      const { data, error, count } = await query;

      if (error) throw error;
      
      setSubscriptionPlans(data || []);
      setFilteredPlans(data || []);
      
      // Also fetch total count for pagination
      const { count: totalCount } = await supabase
        .from('subscription_plans')
        .select('*', { count: 'exact', head: true })
        .eq('is_active', true);
      
      setTotalPlans(totalCount || 0);
    } catch (error) {
      console.error('Error fetching subscription plans:', error);
      toast.error('Falha ao carregar planos de assinatura');
    } finally {
      setLoading(false);
    }
  };

  const fetchPlanFamilies = async () => {
    try {
      const { data, error } = await supabase
        .from('subscription_plan_families')
        .select('id, name')
        .order('name');
      
      if (error) throw error;
      setPlanFamilies(data || []);
    } catch (error) {
      console.error('Error fetching plan families:', error);
    }
  };

  const handleSelectPlan = (plan: SubscriptionPlan) => {
    setSelectedPlanId(plan.id);
      
    // Pre-preencher o preço com base no plano selecionado
    const priceInSelectedCurrency = plan.prices?.find(
      p => p.currency === state.currencyCode
    );
    
    if (priceInSelectedCurrency) {
      setUnitPrice(priceInSelectedCurrency.price_amount);
    } else if (plan.price) {
      setUnitPrice(plan.price);
    } else {
      setUnitPrice(0);
    }
  };

  const handleSearch = () => {
    const filtered = subscriptionPlans.filter(
      plan => plan.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
    setFilteredPlans(filtered);
  };

  const handleAddPlan = () => {
    const selectedPlan = subscriptionPlans.find(p => p.id === selectedPlanId);
    if (!selectedPlan) {
      toast.error('Selecione um plano válido');
      return;
    }

    if (quantity <= 0) {
      toast.error('A quantidade deve ser maior que zero');
      return;
    }

    if (unitPrice < 0) {
      toast.error('O preço unitário não pode ser negativo');
      return;
    }

    const periodValue = parseInt(billingPeriod, 10);
    if (isNaN(periodValue) || periodValue <= 0) {
      toast.error('Informe um período válido');
      return;
    }

    let endDate = new Date(currentTermStart);
    if (billingPeriodUnit === 'day') {
      endDate = new Date(new Date(currentTermStart).setDate(new Date(currentTermStart).getDate() + periodValue));
    } else if (billingPeriodUnit === 'week') {
      endDate = new Date(new Date(currentTermStart).setDate(new Date(currentTermStart).getDate() + (periodValue * 7)));
    } else if (billingPeriodUnit === 'month') {
      endDate = addMonths(new Date(currentTermStart), periodValue);
    } else if (billingPeriodUnit === 'year') {
      endDate = addYears(new Date(currentTermStart), periodValue);
    }

    const newPlan: SelectedPlan = {
      subscription_plan_id: selectedPlanId,
      subscription_plan: selectedPlan,
      quantity,
      unit_price: unitPrice,
      billing_period_unit: billingPeriodUnit,
      billing_period: billingPeriod,
      current_term_start: currentTermStart,
      current_term_end: format(endDate, 'yyyy-MM-dd'),
      billing_start_terms: billingStartTerms,
      auto_renew: autoRenew
    };

    addSelectedPlan(newPlan);
    
    // Reset form
    setSelectedPlanId('');
    setQuantity(1);
    setUnitPrice(0);
    setBillingPeriodUnit('month');
    setBillingPeriod('12');
    setCurrentTermStart(state.startDate);
    setBillingStartTerms('immediately');
    setAutoRenew(true);
    
    setIsAddingPlan(false);
    toast.success('Plano adicionado com sucesso');
  };

  const handleRemovePlan = (index: number) => {
    removeSelectedPlan(index);
    toast.success('Plano removido');
  };

  const handleNextStep = () => {
    navigate('../step6', { relative: 'path' });
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: state.currencyCode,
      minimumFractionDigits: 2
    }).format(value);
  };

  const getTotalPrice = () => {
    return formatCurrency(calculateTotalPrice(state.selectedPlans));
  };

  const hasActiveFilters = advancedFilters.billingFrequencies.length > 0 || advancedFilters.familyIds.length > 0;

  const updateAdvancedFilters = (newFilters: Partial<AdvancedFilters>) => {
    setAdvancedFilters(prev => ({
      ...prev,
      ...newFilters
    }));
    
    // Reset to first page whenever filters change
    setCurrentPage(1);
  };

  const clearAllFilters = () => {
    setAdvancedFilters({
      billingFrequencies: [],
      familyIds: []
    });
    setCurrentPage(1);
  };

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field and reset direction to asc
      setSortField(field);
      setSortDirection('asc');
    }
    
    // Reset to first page on sort change
    setCurrentPage(1);
  };

  return (
    <OpportunityWizardLayout
      currentStep={5}
      totalSteps={7}
      title="Adicionar Produtos"
      description="Adicione os produtos para essa oportunidade"
      onNext={handleNextStep}
      onPrevious={() => navigate('../step4', { relative: 'path' })}
      isNextDisabled={state.selectedPlans.length === 0}
    >
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-lg font-extrabold">
              Cliente: {state.selectedCustomer?.name}
            </h2>
          </div>
          <Button onClick={() => setIsAddPlanDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Adicionar Plano
          </Button>
        </div>

        {/* Métricas Financeiras */}
        <div>
          <h3 className="text-lg font-extrabold mb-4">Métricas Financeiras</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex justify-between items-start mb-2">
                  <div className="font-extrabold">MRR</div>
                  <div className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full">Mensal</div>
                </div>
                <div className="text-2xl font-extrabold mb-1">
                  {formatCurrency(calculateTotalPrice(state.selectedPlans) / 12)}
                </div>
                <div className="text-xs text-muted-foreground">Monthly Recurring Revenue</div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex justify-between items-start mb-2">
                  <div className="font-extrabold">ARR</div>
                  <div className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full">Anual</div>
                </div>
                <div className="text-2xl font-extrabold mb-1">
                  {formatCurrency(calculateTotalPrice(state.selectedPlans))}
                </div>
                <div className="text-xs text-muted-foreground">Annual Recurring Revenue</div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex justify-between items-start mb-2">
                  <div className="font-extrabold">ACV</div>
                  <div className="text-xs bg-purple-100 text-purple-800 px-2 py-0.5 rounded-full">Primeiro Ano</div>
                </div>
                <div className="text-2xl font-extrabold mb-1">
                  {formatCurrency(calculateTotalPrice(state.selectedPlans))}
                </div>
                <div className="text-xs text-muted-foreground">Annual Contract Value</div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex justify-between items-start mb-2">
                  <div className="font-extrabold">TCV</div>
                  <div className="text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded-full">Duração Total</div>
                </div>
                <div className="text-2xl font-extrabold mb-1">
                  {formatCurrency(calculateTotalPrice(state.selectedPlans))}
                </div>
                <div className="text-xs text-muted-foreground">Total Contract Value</div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Resumo de valores */}
        <Card className="bg-muted/50">
          <CardContent className="p-4">
            <div className="flex justify-between items-center">
              <div>
                <div className="text-sm text-muted-foreground">Valor Total:</div>
                <div className="text-2xl font-extrabold">{getTotalPrice()}</div>
              </div>
              <div className="text-sm text-muted-foreground">
                {state.selectedPlans.length} {state.selectedPlans.length === 1 ? 'produto' : 'produtos'}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Lista de produtos selecionados */}
        <div className="space-y-3">
          {state.selectedPlans.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              Nenhum produto adicionado. Clique em "Adicionar Plano" para começar.
            </div>
          ) : (
            <div className="border rounded-md overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Plano</TableHead>
                    <TableHead className="text-center">Quantidade</TableHead>
                    <TableHead>Preço Unitário</TableHead>
                    <TableHead className="text-center">Fidelidade</TableHead>
                    <TableHead>Vigência</TableHead>
                    <TableHead>Total</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {state.selectedPlans.map((plan, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <div className="flex flex-col">
                          <div className="flex items-center gap-1">
                            <span className="font-extrabold">{plan.subscription_plan.name}</span>
                            {plan.subscription_plan.description && (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Info className="h-4 w-4 text-muted-foreground" />
                                  </TooltipTrigger>
                                  <TooltipContent className="max-w-xs">
                                    <p>{plan.subscription_plan.description}</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                          </div>
                          {plan.subscription_plan.sku && (
                            <div className="text-xs text-muted-foreground">
                              SKU: {plan.subscription_plan.sku}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-center">{plan.quantity}</TableCell>
                      <TableCell>{formatCurrency(plan.unit_price)}{plan.billing_period_unit === 'year' ? '/year' : plan.billing_period_unit === 'month' ? '/month' : ''}</TableCell>
                      <TableCell className="text-center">
                        <Badge variant="secondary" className="rounded-full">
                          {plan.billing_period_unit === 'year' ? `${plan.billing_period} ano` : 
                           plan.billing_period_unit === 'month' ? `${plan.billing_period} mês` : 
                           plan.billing_period_unit === 'week' ? `${plan.billing_period} semana` : 
                           `${plan.billing_period} dia`}
                          {parseInt(plan.billing_period) > 1 ? 
                            (plan.billing_period_unit === 'year' ? 's' : 
                             plan.billing_period_unit === 'month' ? 'es' : 
                             plan.billing_period_unit === 'week' ? 's' : 's') : ''}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col text-sm">
                          <div>{format(new Date(plan.current_term_start), 'dd/MM/yyyy')} a {format(new Date(plan.current_term_end), 'dd/MM/yyyy')}</div>
                          <div className="text-xs text-muted-foreground">
                            Início: {plan.billing_start_terms === 'immediately' ? 'Imediatamente' : 'Data personalizada'}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            Renovação: {plan.auto_renew ? 'Automática' : 'Manual'}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="font-extrabold">
                        {formatCurrency(plan.unit_price * plan.quantity)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex space-x-1 justify-end">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => handleRemovePlan(index)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </div>

      {/* Modal de adição de produto */}
      <Dialog open={isAddPlanDialogOpen} onOpenChange={setIsAddPlanDialogOpen}>
        <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Adicionar Plano</DialogTitle>
          </DialogHeader>
          
          {!selectedPlanId ? (
            <>
              <div className="flex items-center gap-2 mb-4">
                <div className="relative flex-1">
                  <Input
                    placeholder="Pesquisar plano por nome ou SKU"
                    value={planSearchTerm}
                    onChange={(e) => {
                      setPlanSearchTerm(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="pr-10"
                  />
                  {planSearchTerm ? (
                    <button 
                      type="button"
                      onClick={() => {
                        setPlanSearchTerm('');
                        setCurrentPage(1);
                      }}
                      className="absolute right-10 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground hover:text-foreground"
                      title="Limpar pesquisa"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  ) : null}
                  <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                </div>
                
                <Popover open={isFilterOpen} onOpenChange={setIsFilterOpen}>
                  <PopoverTrigger asChild>
                    <Button 
                      variant="outline" 
                      className="gap-2"
                      aria-label="Filtros avançados"
                    >
                      <SlidersHorizontal className="h-4 w-4" />
                      <span className="hidden sm:inline">Filtros</span>
                      {hasActiveFilters && (
                        <Badge variant="secondary" className="ml-1 h-5 px-1">
                          {advancedFilters.familyIds.length + 
                           advancedFilters.billingFrequencies.length}
                        </Badge>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-80">
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <h4 className="font-extrabold">Filtros Avançados</h4>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={clearAllFilters}
                          className="h-8 px-2 text-xs"
                        >
                          Limpar Filtros
                        </Button>
                      </div>
                      
                      <Separator />
                      
                      <div className="space-y-2">
                        <h5 className="text-sm font-extrabold">Frequência de Cobrança</h5>
                        <ScrollArea className="h-24">
                          <div className="space-y-2">
                            {['monthly', 'yearly', 'quarterly', 'semiannually'].map((freq) => (
                              <div key={freq} className="flex items-center space-x-2">
                                <Checkbox 
                                  id={`freq-${freq}`} 
                                  checked={advancedFilters.billingFrequencies.includes(freq)}
                                  onCheckedChange={(checked) => {
                                    if (checked) {
                                      updateAdvancedFilters({
                                        billingFrequencies: [...advancedFilters.billingFrequencies, freq]
                                      });
                                    } else {
                                      updateAdvancedFilters({
                                        billingFrequencies: advancedFilters.billingFrequencies.filter(f => f !== freq)
                                      });
                                    }
                                  }}
                                />
                                <Label htmlFor={`freq-${freq}`} className="text-sm">
                                  {translateBillingFrequency(freq)}
                                </Label>
                              </div>
                            ))}
                          </div>
                        </ScrollArea>
                      </div>

                      <Separator />
                      
                      <div className="space-y-2">
                        <h5 className="text-sm font-extrabold">Famílias de Planos</h5>
                        <ScrollArea className="h-32">
                          <div className="space-y-2">
                            {planFamilies.map((family) => (
                              <div key={family.id} className="flex items-center space-x-2">
                                <Checkbox 
                                  id={`family-${family.id}`} 
                                  checked={advancedFilters.familyIds.includes(family.id)}
                                  onCheckedChange={(checked) => {
                                    if (checked) {
                                      updateAdvancedFilters({
                                        familyIds: [...advancedFilters.familyIds, family.id]
                                      });
                                    } else {
                                      updateAdvancedFilters({
                                        familyIds: advancedFilters.familyIds.filter(id => id !== family.id)
                                      });
                                    }
                                  }}
                                />
                                <Label htmlFor={`family-${family.id}`} className="text-sm">
                                  {family.name}
                                </Label>
                              </div>
                            ))}
                          </div>
                        </ScrollArea>
                      </div>
                      
                      <Button 
                        className="w-full" 
                        onClick={() => setIsFilterOpen(false)}
                      >
                        Aplicar Filtros
                      </Button>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
              
              <div className="flex-1 overflow-hidden flex flex-col">
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    <span className="ml-2">Carregando planos...</span>
                  </div>
                ) : filteredPlans.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    Nenhum plano encontrado com os filtros atuais
                  </div>
                ) : (
                  <div className="flex-1 overflow-hidden flex flex-col">
                    <div className="border rounded-md overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-[40px]"></TableHead>
                            <TableHead>
                              <Button 
                                variant="ghost" 
                                className="p-0 h-auto font-extrabold flex items-center gap-1"
                                onClick={() => toggleSort('name')}
                              >
                                Plano
                                <ArrowUpDown className="h-3 w-3" />
                              </Button>
                            </TableHead>
                            <TableHead>
                              <Button 
                                variant="ghost" 
                                className="p-0 h-auto font-extrabold flex items-center gap-1"
                                onClick={() => toggleSort('price')}
                              >
                                Preço
                                <ArrowUpDown className="h-3 w-3" />
                              </Button>
                            </TableHead>
                            <TableHead className="w-[100px] text-center">Fidelidade</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {filteredPlans.map((plan) => {
                            const isSelected = selectedPlanId === plan.id;
                            return (
                              <TableRow 
                                key={plan.id} 
                                className={`cursor-pointer ${isSelected ? 'bg-primary/5' : ''}`}
                                onClick={() => handleSelectPlan(plan)}
                              >
                                <TableCell className="p-2">
                                  <div className="flex justify-center">
                                    {isSelected && <Check className="h-4 w-4 text-primary" />}
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <div className="flex flex-col">
                                    <div className="flex items-center gap-1">
                                      <span className="font-extrabold">{plan.name}</span>
                                      {plan.description && (
                                        <TooltipProvider>
                                          <Tooltip>
                                            <TooltipTrigger asChild>
                                              <button 
                                                className="inline-flex items-center justify-center rounded-full h-4 w-4 text-muted-foreground hover:text-foreground"
                                                onClick={(e) => e.stopPropagation()}
                                              >
                                                <Info className="h-3 w-3" />
                                              </button>
                                            </TooltipTrigger>
                                            <TooltipContent className="max-w-xs">
                                              <p>{plan.description}</p>
                                            </TooltipContent>
                                          </Tooltip>
                                        </TooltipProvider>
                                      )}
                                    </div>
                                    {plan.subscription_plan_families?.name && (
                                      <span className="text-xs text-muted-foreground">
                                        {plan.subscription_plan_families.name}
                                      </span>
                                    )}
                                    {plan.sku && (
                                      <span className="text-xs text-muted-foreground">
                                        SKU: {plan.sku}
                                      </span>
                                    )}
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <div>
                                    {plan.prices && plan.prices.length > 0 ? (
                                      <div className="font-extrabold">
                                        {formatCurrency(plan.prices[0].price_amount)}
                                        {plan.billing_frequency && (
                                          <span className="text-xs text-muted-foreground ml-1">
                                            /{plan.billing_frequency.toLowerCase()}
                                          </span>
                                        )}
                                      </div>
                                    ) : (
                                      <div className="font-extrabold">
                                        {formatCurrency(plan.price)}
                                      </div>
                                    )}
                                  </div>
                                </TableCell>
                                <TableCell className="text-center">
                                  <div className="text-xs text-muted-foreground">
                                    {plan.billing_frequency ? translateBillingFrequency(plan.billing_frequency.toLowerCase()) : 'Não se aplica'}
                                  </div>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                    
                    <div className="flex justify-center mt-4">
                      <Pagination>
                        <PaginationContent>
                          <PaginationItem>
                            <PaginationPrevious 
                              href="#" 
                              onClick={(e) => {
                                e.preventDefault();
                                if (currentPage > 1) setCurrentPage(currentPage - 1);
                              }}
                            />
                          </PaginationItem>
                          
                          {Array.from({ length: Math.min(5, Math.ceil(filteredPlans.length / ITEMS_PER_PAGE)) }).map((_, i) => (
                            <PaginationItem key={i}>
                              <PaginationLink 
                                href="#" 
                                isActive={currentPage === i + 1}
                                onClick={(e) => {
                                  e.preventDefault();
                                  setCurrentPage(i + 1);
                                }}
                              >
                                {i + 1}
                              </PaginationLink>
                            </PaginationItem>
                          ))}
                          
                          <PaginationItem>
                            <PaginationNext 
                              href="#" 
                              onClick={(e) => {
                                e.preventDefault();
                                if (currentPage < Math.ceil(filteredPlans.length / ITEMS_PER_PAGE)) {
                                  setCurrentPage(currentPage + 1);
                                }
                              }}
                            />
                          </PaginationItem>
                        </PaginationContent>
                      </Pagination>
                    </div>
                    
                    <div className="text-xs text-center text-muted-foreground mt-2">
                      Mostrando 1 a {Math.min(ITEMS_PER_PAGE, filteredPlans.length)} de {filteredPlans.length} planos
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <div className="space-y-6 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="quantity">Quantidade</Label>
                    <Input
                      id="quantity"
                      type="number"
                      min="1"
                      value={quantity}
                      onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="unit-price">Preço Unitário ({state.currencyCode})</Label>
                    <Input
                      id="unit-price"
                      type="number"
                      min="0"
                      step="0.01"
                      value={unitPrice}
                      onChange={(e) => setUnitPrice(parseFloat(e.target.value) || 0)}
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Unidade de Período</Label>
                    <Select
                      value={billingPeriodUnit}
                      onValueChange={setBillingPeriodUnit}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione a unidade" />
                      </SelectTrigger>
                      <SelectContent>
                        {BILLING_PERIOD_UNITS.map((unit) => (
                          <SelectItem key={unit.value} value={unit.value}>
                            {unit.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="billing-period">Período</Label>
                    <Input
                      id="billing-period"
                      type="number"
                      min="1"
                      value={billingPeriod}
                      onChange={(e) => setBillingPeriod(e.target.value)}
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="current-term-start">Data de Início</Label>
                    <Input
                      id="current-term-start"
                      type="date"
                      value={currentTermStart}
                      onChange={(e) => setCurrentTermStart(e.target.value)}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>Renovação Automática</Label>
                      <div>
                        <RadioGroup
                          value={autoRenew ? "yes" : "no"}
                          onValueChange={(value) => setAutoRenew(value === "yes")}
                          className="flex gap-4"
                        >
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="yes" id="auto-renew-yes" />
                            <Label htmlFor="auto-renew-yes" className="cursor-pointer">Sim</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="no" id="auto-renew-no" />
                            <Label htmlFor="auto-renew-no" className="cursor-pointer">Não</Label>
                          </div>
                        </RadioGroup>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <DialogFooter className="flex justify-between">
                <div>
                  <div className="font-extrabold">
                    Total: {formatCurrency(unitPrice * quantity)}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setSelectedPlanId('');
                    }}
                  >
                    Voltar
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setIsAddPlanDialogOpen(false)}
                  >
                    Cancelar
                  </Button>
                  <Button
                    onClick={handleAddPlan}
                  >
                    Adicionar
                  </Button>
                </div>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </OpportunityWizardLayout>
  );
};

export default Step5Plans;